import { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import ProgressHeatmap from '@/components/ProgressHeatmap';
import { 
  ArrowLeft, IndianRupee, FileText, Users, CheckSquare, Clock, 
  AlertTriangle, TrendingUp, TrendingDown, BarChart3, PieChart,
  FileCheck, Receipt, UserCheck, Activity, RefreshCw, Wallet, ShieldCheck, Lock, Unlock,
  Upload, Link, FolderOpen, Image, FileSpreadsheet, Trash2, ExternalLink, Plus, Edit,
  CreditCard, Banknote, Mail, CalendarClock
} from 'lucide-react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import PaymentReminders from '@/components/PaymentReminders';
import ProfitabilityTrendCard from '@/components/dashboard/ProfitabilityTrendCard';
import CommissionPreviewChip from '@/components/CommissionPreviewChip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend
} from 'recharts';
import { formatDate } from '@/utils/dateFormat';

// iter-386u-bu — Themable palette: first slot pulls from tenant brand color,
// remaining slots stay high-contrast so multi-series charts still read cleanly.
const COLORS = ['var(--brand-button, #D4A017)', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function ProjectDashboard({ embedded = false }) {
  // Embedded = rendered inline inside the project page (Financial Overview tab).
  const Shell = embedded ? Fragment : Layout;
  const { id } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [fundLedger, setFundLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['owner', 'staff', 'admin', 'project_manager'].includes(user.role);
  const isVendor = ['vendor', 'labour'].includes(user.role);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('link'); // 'link', 'upload', 'gdrive', 'onedrive'
  const [documentForm, setDocumentForm] = useState({
    name: '',
    url: '',
    doc_type: 'drawing',
    source: 'link'
  });
  const [uploading, setUploading] = useState(false);
  
  // Token revert state
  const [revertTokenDialogOpen, setRevertTokenDialogOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);
  
  // Payment tracking state
  const [paymentReceivedDialogOpen, setPaymentReceivedDialogOpen] = useState(false);
  // iter-323: Heatmap data — flat BOQ items aggregated across all linked BOQs
  const [heatmapItems, setHeatmapItems] = useState([]);
  const [heatmapBoqId, setHeatmapBoqId] = useState(null);
  const [vendorPaymentDialogOpen, setVendorPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentMode, setPaymentMode] = useState('online');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [vendorPaymentName, setVendorPaymentName] = useState('');
  const [vendorPaymentId, setVendorPaymentId] = useState('');
  const [allVendors, setAllVendors] = useState([]);
  const [showCustomVendorInput, setShowCustomVendorInput] = useState(false);
  const [showCreateVendorForm, setShowCreateVendorForm] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', email: '', phone: '', company_name: '' });
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [selectedPaymentStage, setSelectedPaymentStage] = useState(null);
  
  // Payment Ledger state
  const [paymentLedger, setPaymentLedger] = useState(null);
  const [paymentLedgerDialogOpen, setPaymentLedgerDialogOpen] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [amendReason, setAmendReason] = useState('');
  const [editPaymentMode, setEditPaymentMode] = useState('online');
  const [editPaymentDate, setEditPaymentDate] = useState('');
  const [amendVendorName, setAmendVendorName] = useState('');
  const [amendVendorId, setAmendVendorId] = useState('');
  
  // Retention Release state
  const [releaseRetentionDialogOpen, setReleaseRetentionDialogOpen] = useState(false);
  const [retentionReleaseAmount, setRetentionReleaseAmount] = useState('');
  const [retentionReleaseNotes, setRetentionReleaseNotes] = useState('');
  const [releasingRetention, setReleasingRetention] = useState(false);

  // Email Compose state
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [emailTemplateType, setEmailTemplateType] = useState('payment_request');

  useEffect(() => {
    fetchDashboard();
    fetchFundLedger();
    fetchDocuments();
    fetchPaymentLedger();
    fetchHeatmapData();
  }, [id]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load project dashboard');
      navigate('/projects');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch all vendors for payment dialogs
  const fetchAllVendors = async () => {
    if (allVendors.length > 0) return;
    try {
      const resp = await axios.get(`${API}/vendors/directory`);
      setAllVendors(resp.data || []);
    } catch {}
  };

  // Create new vendor inline from payment dialog
  const handleCreateVendorInline = async () => {
    if (!newVendor.name?.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    setCreatingVendor(true);
    try {
      const vendorEmail = newVendor.email?.trim() || `vendor_${Date.now()}@placeholder.com`;
      const resp = await axios.post(`${API}/auth/register`, {
        name: newVendor.name.trim(),
        email: vendorEmail,
        password: `Vendor${Date.now()}!`,
        role: 'vendor',
        phone: newVendor.phone || '',
        organization: newVendor.company_name || '',
      });
      const createdUser = resp.data.user;
      // Update company_name in user master data if provided
      if (newVendor.company_name) {
        try {
          await axios.patch(`${API}/users/${createdUser.id}`, {
            company_name: newVendor.company_name,
            master_data: { company_name: newVendor.company_name }
          });
        } catch {}
      }
      toast.success(`Vendor "${newVendor.name}" created successfully`);
      // Set this new vendor as selected
      setVendorPaymentId(createdUser.id);
      setVendorPaymentName(createdUser.name);
      setShowCreateVendorForm(false);
      setShowCustomVendorInput(false);
      setNewVendor({ name: '', email: '', phone: '', company_name: '' });
      // Refresh vendors list
      try {
        const vendorResp = await axios.get(`${API}/vendors/directory`);
        setAllVendors(vendorResp.data || []);
      } catch {}
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create vendor';
      if (detail.includes('already registered')) {
        toast.error('A user with this email already exists. Select them from the dropdown instead.');
      } else {
        toast.error(detail);
      }
    } finally {
      setCreatingVendor(false);
    }
  };


  const fetchFundLedger = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/fund-ledger`);
      setFundLedger(response.data);
    } catch (error) {
      console.error('Failed to load fund ledger:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const fetchPaymentLedger = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/payment-ledger`);
      setPaymentLedger(response.data);
    } catch (error) {
      console.error('Failed to load payment ledger:', error);
    }
  };

  // iter-323: Aggregate BOQ items across all worksheets linked to this project
  // for the Progress Heatmap widget.
  const fetchHeatmapData = async () => {
    try {
      const res = await axios.get(`${API}/boq-worksheets?project_id=${id}&active_filter=active`);
      const list = res.data || [];
      if (!list.length) { setHeatmapItems([]); return; }
      // Prefer the most-recent active BOQ
      const ws = list[0];
      setHeatmapBoqId(ws.id);
      // Fetch full BOQ to get items
      const wsRes = await axios.get(`${API}/boq-worksheets/${ws.id}`);
      setHeatmapItems(wsRes.data?.items || []);
    } catch (e) {
      console.error('Heatmap fetch failed:', e);
      setHeatmapItems([]);
    }
  };

  // Handle delete payment
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Payment deleted successfully');
      fetchPaymentLedger();
      fetchFundLedger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  // Handle amend payment
  const handleAmendPayment = async () => {
    if (!selectedPayment || !amendReason.trim()) {
      toast.error('Please provide a reason for amendment');
      return;
    }
    
    setPaymentProcessing(true);
    try {
      await axios.put(`${API}/payments/${selectedPayment.id}/amend`, {
        amount: parseFloat(paymentAmount) || selectedPayment.amount,
        notes: paymentNotes || selectedPayment.notes,
        payment_mode: editPaymentMode,
        payment_date: editPaymentDate ? new Date(editPaymentDate).toISOString() : undefined,
        amend_reason: amendReason,
        vendor_name: amendVendorName || selectedPayment.vendor_name,
        vendor_id: amendVendorId || selectedPayment.vendor_id,
      });
      toast.success('Payment amended successfully');
      setEditPaymentDialogOpen(false);
      setSelectedPayment(null);
      setAmendReason('');
      setPaymentAmount('');
      setPaymentNotes('');
      setEditPaymentMode('online');
      setEditPaymentDate('');
      setAmendVendorName('');
      setAmendVendorId('');
      fetchPaymentLedger();
      fetchFundLedger();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to amend payment');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Download receipt
  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt-pdf`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download receipt');
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!documentForm.name || !documentForm.url) {
      toast.error('Please provide document name and URL');
      return;
    }
    
    setUploading(true);
    try {
      await axios.post(`${API}/projects/${id}/documents`, {
        name: documentForm.name,
        url: documentForm.url,
        doc_type: documentForm.doc_type,
        source: uploadType
      });
      toast.success('Document added successfully');
      setDocumentDialogOpen(false);
      setDocumentForm({ name: '', url: '', doc_type: 'drawing', source: 'link' });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/projects/${id}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // For now, show instructions to use cloud storage
    toast.info('File upload coming soon. Please use Google Drive or OneDrive linking for now.');
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
    fetchFundLedger();
  };

  const handleMarkTokenReceived = async () => {
    try {
      await axios.post(`${API}/projects/${id}/mark-token-received`, {
        notes: 'Marked via Project Dashboard'
      });
      toast.success('Token payment marked as received. Execution is now UNLOCKED!');
      fetchFundLedger();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark token received');
    }
  };

  // iter-323: Admin override — force-unlock without changing payment ledger
  const handleForceUnlockExecution = async () => {
    if (!window.confirm('Force-unlock execution? This stamps an admin override on the project. Use only if the auto-heal didn\'t detect a valid token payment.')) return;
    try {
      await axios.post(`${API}/projects/${id}/force-unlock-execution`);
      toast.success('Execution unlocked (admin override). Audit stamp added.');
      fetchFundLedger();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to override execution lock');
    }
  };

  const handleRevertTokenReceived = async () => {
    if (!revertReason || revertReason.trim().length < 10) {
      toast.error('Please provide a reason (at least 10 characters)');
      return;
    }
    setReverting(true);
    try {
      await axios.post(`${API}/projects/${id}/revert-token-received`, {
        reason: revertReason.trim()
      });
      toast.success('Token payment status reverted. Execution is now LOCKED.');
      setRevertTokenDialogOpen(false);
      setRevertReason('');
      fetchFundLedger();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert token status');
    } finally {
      setReverting(false);
    }
  };

  // Handle recording client payment received
  const handleRecordPaymentReceived = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    setPaymentProcessing(true);
    try {
      await axios.post(`${API}/projects/${id}/record-client-payment`, {
        amount: parseFloat(paymentAmount),
        notes: paymentNotes || 'Payment recorded via Project Dashboard',
        payment_type: 'client_received',
        payment_mode: paymentMode,
        payment_date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString()
      });
      toast.success(`${paymentMode === 'cash' ? 'Cash' : 'Online'} payment of ₹${parseFloat(paymentAmount).toLocaleString('en-IN')} recorded successfully`);
      setPaymentReceivedDialogOpen(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentMode('online');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      fetchFundLedger();
      fetchDashboard();
      fetchPaymentLedger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Handle recording vendor payment done
  const handleRecordVendorPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    setPaymentProcessing(true);
    try {
      await axios.post(`${API}/projects/${id}/record-vendor-payment`, {
        amount: parseFloat(paymentAmount),
        vendor_name: vendorPaymentName || '',
        vendor_id: vendorPaymentId || '',
        notes: paymentNotes || '',
        payment_type: 'vendor_paid',
        payment_mode: paymentMode,
        payment_date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString()
      });
      toast.success(`${paymentMode === 'cash' ? 'Cash' : 'Online'} vendor payment of ₹${parseFloat(paymentAmount).toLocaleString('en-IN')} recorded successfully`);
      setVendorPaymentDialogOpen(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setVendorPaymentName('');
      setVendorPaymentId('');
      setShowCustomVendorInput(false);
      setPaymentMode('online');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      fetchFundLedger();
      fetchDashboard();
      fetchPaymentLedger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record vendor payment');
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Handle release retention amount
  const handleReleaseRetention = async () => {
    const amount = parseFloat(retentionReleaseAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid release amount');
      return;
    }
    
    // Check against available retention
    if (fundLedger?.retention_held && amount > fundLedger.retention_held) {
      toast.error(`Cannot release more than held retention (${formatCurrency(fundLedger.retention_held)})`);
      return;
    }
    
    setReleasingRetention(true);
    try {
      await axios.post(`${API}/projects/${id}/release-retention`, {
        amount: amount,
        notes: retentionReleaseNotes || 'Retention released as per project completion'
      });
      toast.success(`Retention of ${formatCurrency(amount)} released successfully`);
      setReleaseRetentionDialogOpen(false);
      setRetentionReleaseAmount('');
      setRetentionReleaseNotes('');
      fetchFundLedger();
      fetchPaymentLedger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release retention');
    } finally {
      setReleasingRetention(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      delayed: 'bg-red-100 text-red-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-600',
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center h-64" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      </Shell>
    );
  }

  if (!dashboard) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-stone-500">Failed to load dashboard</p>
          <Button onClick={() => navigate('/projects')} variant="outline" className="mt-4">
            Back to Projects
          </Button>
        </div>
      </Shell>
    );
  }

  // Prepare chart data
  const categoryData = dashboard.cost_summary?.category_breakdown?.map(c => ({
    name: c.category?.substring(0, 15) || 'Other',
    value: c.amount,
    count: c.count
  })) || [];

  const costComparisonData = dashboard.cost_summary ? [
    { name: 'Budget', value: dashboard.cost_summary.budget, fill: '#D4A017' },
    { name: 'BOQ (Planned)', value: dashboard.cost_summary.boq_planned, fill: '#3B82F6' },
    { name: 'Committed', value: dashboard.cost_summary.quotations_committed, fill: '#10B981' },
    { name: 'Invoiced', value: dashboard.cost_summary.invoiced_total, fill: '#F59E0B' },
    { name: 'Paid', value: dashboard.cost_summary.payments_completed, fill: '#8B5CF6' },
  ] : [];

  const taskStatusData = [
    { name: 'Not Started', value: dashboard.task_summary?.not_started || 0, fill: '#9CA3AF' },
    { name: 'In Progress', value: dashboard.task_summary?.in_progress || 0, fill: '#3B82F6' },
    { name: 'Completed', value: dashboard.task_summary?.completed || 0, fill: '#10B981' },
    { name: 'Delayed', value: dashboard.task_summary?.delayed || 0, fill: '#EF4444' },
  ].filter(d => d.value > 0);

  return (
    <Shell>
      <div className="space-y-6" data-testid="project-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!embedded && (
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/projects/${id}`)}
              className="p-2"
            >
              <ArrowLeft size={20} />
            </Button>
            )}
            <div>
              <h1 className={embedded ? "font-playfair text-xl font-bold text-stone-900" : "font-playfair text-3xl font-bold text-stone-900"}>
                {embedded ? "Financial Overview" : dashboard.project_name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusColor(dashboard.project_status)}>
                  {dashboard.project_status?.replace('_', ' ')}
                </Badge>
                <span className="text-stone-500 text-sm">{embedded ? 'Fund ledger, payments and reminders' : 'Project Overview'}</span>
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>

        {/* Quick Stats Row - All items clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {isAdmin && dashboard.cost_summary && (
            <>
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/projects/${id}?tab=boq`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Budget</p>
                      <p className="text-2xl font-bold text-stone-900">
                        {formatCurrency(dashboard.cost_summary.budget)}
                      </p>
                    </div>
                    <IndianRupee className="h-8 w-8 text-amber-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow"
                data-testid="variance-card"
                onClick={() => {
                  setActiveTab('costs');
                  setTimeout(() => {
                    document.querySelector('[data-testid="project-dashboard-tabs"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500">Variance</p>
                      <p className={`text-2xl font-bold ${dashboard.cost_summary.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {dashboard.cost_summary.variance >= 0 ? '+' : ''}{formatCurrency(dashboard.cost_summary.variance)}
                      </p>
                      <p className="text-xs text-stone-400">
                        {dashboard.cost_summary.variance_percentage >= 0 ? '+' : ''}{dashboard.cost_summary.variance_percentage}%
                      </p>
                    </div>
                    {dashboard.cost_summary.variance >= 0 ? 
                      <TrendingUp className="h-8 w-8 text-green-500 opacity-50" /> :
                      <TrendingDown className="h-8 w-8 text-red-500 opacity-50" />
                    }
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            data-testid="pending-approvals-card"
            onClick={() => {
              setActiveTab('approvals');
              setTimeout(() => {
                document.querySelector('[data-testid="project-dashboard-tabs"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Pending Approvals</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {dashboard.pending_counts?.total || 0}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            data-testid="task-progress-card"
            onClick={() => navigate(`/tasks?project=${id}`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Task Progress</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {dashboard.task_summary?.completion_percentage || 0}%
                  </p>
                </div>
                <CheckSquare className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FUND LEDGER - Commercial Control System */}
        {fundLedger && isAdmin && (
          <Card className={`border-2 ${fundLedger.fund_status === 'red' ? 'border-red-400 bg-red-50' : fundLedger.fund_status === 'yellow' ? 'border-yellow-400 bg-yellow-50' : 'border-green-400 bg-green-50'}`} data-testid="fund-ledger">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Wallet size={20} />
                  Project Fund Ledger
                </CardTitle>
                {/* Execution Status Badge */}
                {fundLedger.execution_locked ? (
                  <Badge variant="destructive" className="flex items-center gap-1 bg-red-600">
                    <Lock size={14} /> Execution Locked
                  </Badge>
                ) : (
                  <Badge className="flex items-center gap-1 bg-green-600">
                    <Unlock size={14} /> Execution Unlocked
                  </Badge>
                )}
              </div>
              {fundLedger.execution_locked && (
                <p className="text-sm text-red-700 mt-1">
                  5% token payment required to unlock execution
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Token Status */}
              {!fundLedger.token_received && (
                <div className="p-4 bg-orange-100 rounded-lg border border-orange-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-orange-800">Token Payment Pending</p>
                      <p className="text-sm text-orange-700">
                        Amount: {formatCurrency(fundLedger.token_amount)} ({fundLedger.token_percentage}%)
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <Button 
                        onClick={handleMarkTokenReceived}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="mark-token-received-btn"
                      >
                        <ShieldCheck size={16} className="mr-2" />
                        Mark Token Received
                      </Button>
                      {/* iter-323: One-click override for legacy / drifted projects */}
                      {(user.role === 'owner' || user.role === 'admin') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleForceUnlockExecution}
                          className="text-stone-700 hover:bg-stone-50"
                          data-testid="force-unlock-execution-btn"
                          title="Override the gate manually (uses payment ledger; admins only)"
                        >
                          Unlock Execution Override
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Token Received - Show status with revert option (Owner only) */}
              {fundLedger.token_received && (
                <div className="p-4 bg-green-100 rounded-lg border border-green-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-green-800 flex items-center gap-2">
                        <ShieldCheck size={18} />
                        Token Payment Received
                      </p>
                      <p className="text-sm text-green-700">
                        Amount: {formatCurrency(fundLedger.token_amount)} ({fundLedger.token_percentage}%) - Execution Unlocked
                      </p>
                    </div>
                    {user.role === 'owner' && (
                      <Button 
                        variant="outline"
                        onClick={() => setRevertTokenDialogOpen(true)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Lock size={16} className="mr-2" />
                        Revert Token Status
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Fund Summary Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-xs text-stone-500 uppercase">Contract Value</p>
                  <p className="text-xl font-bold text-stone-900">{formatCurrency(fundLedger.contract_value)}</p>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-xs text-stone-500 uppercase">Client Received</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(fundLedger.total_client_received)}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 rounded">Online: {formatCurrency(fundLedger.client_online_received || 0)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">Cash: {formatCurrency(fundLedger.client_cash_received || 0)}</span>
                  </div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-xs text-stone-500 uppercase">Vendor Paid</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(fundLedger.total_vendor_paid)}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 rounded">Online: {formatCurrency(fundLedger.vendor_online_paid || 0)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">Cash: {formatCurrency(fundLedger.vendor_cash_paid || 0)}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg shadow-sm ${
                  fundLedger.fund_status === 'red' ? 'bg-red-100' : 
                  fundLedger.fund_status === 'yellow' ? 'bg-stone-100' : 'bg-green-100'
                }`}>
                  <p className="text-xs text-stone-500 uppercase">Available Fund</p>
                  <p className={`text-xl font-bold ${
                    fundLedger.fund_status === 'red' ? 'text-red-700' : 
                    fundLedger.fund_status === 'yellow' ? 'text-stone-700' : 'text-green-700'
                  }`}>
                    {formatCurrency(fundLedger.available_fund)}
                    <span className="text-sm font-normal ml-1">({fundLedger.fund_percentage}%)</span>
                  </p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/60 text-blue-700 rounded">Online: {formatCurrency(fundLedger.available_online || 0)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/60 text-amber-700 rounded">Cash: {formatCurrency(fundLedger.available_cash || 0)}</span>
                  </div>
                </div>
              </div>
              
              {/* Payment Action Buttons */}
              {(user.role === 'owner' || user.role === 'admin') && (
                <div className="flex flex-wrap gap-3 pt-4 border-t border-stone-200">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setPaymentAmount('');
                      setPaymentNotes('');
                      setPaymentReceivedDialogOpen(true);
                    }}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <Plus size={16} className="mr-2" />
                    Record Client Payment
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      setPaymentAmount('');
                      setPaymentNotes('');
                      setVendorPaymentName('');
                      setVendorPaymentId('');
                      setShowCustomVendorInput(false);
                      setVendorPaymentDialogOpen(true);
                      // Fetch all system vendors
                      try {
                        const authToken = localStorage.getItem('token');
                        const resp = await axios.get(`${API}/vendors/directory`, {
                          headers: { Authorization: `Bearer ${authToken}` }
                        });
                        setAllVendors(resp.data || []);
                      } catch {}
                    }}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Plus size={16} className="mr-2" />
                    Record Vendor Payment
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setPaymentLedgerDialogOpen(true)}
                    className="border-stone-300 text-stone-700 hover:bg-stone-50"
                  >
                    <Receipt size={16} className="mr-2" />
                    View Payment Ledger
                  </Button>
                  {isAdmin && (
                    <Button 
                      variant="outline"
                      onClick={() => { setEmailTemplateType('payment_request'); setEmailComposeOpen(true); }}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="project-generate-payment-request-always"
                    >
                      <Mail size={16} className="mr-2" />
                      Payment Request
                    </Button>
                  )}
                  {isAdmin && (
                    <Button 
                      variant="outline"
                      onClick={() => { setEmailTemplateType('project_update'); setEmailComposeOpen(true); }}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="project-send-update-email"
                    >
                      <Mail size={16} className="mr-2" />
                      Project Update
                    </Button>
                  )}
                </div>
              )}

              {/* Additional Details Row */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2 border-t">
                <div className="text-sm">
                  <p className="text-stone-500">Retention Held (Client)</p>
                  <p className="font-semibold">{formatCurrency(fundLedger.retention_held || 0)}</p>
                  <p className="text-xs text-stone-400">Part of contract, usable for vendor payments</p>
                  {(fundLedger.retention_held || 0) > 0 && isAdmin && fundLedger.retention_releasable && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-2 text-xs"
                      onClick={() => {
                        setRetentionReleaseAmount((fundLedger.retention_held || 0).toString());
                        setReleaseRetentionDialogOpen(true);
                      }}
                      data-testid="release-retention-btn"
                    >
                      <Unlock size={12} className="mr-1" />
                      Release Retention
                    </Button>
                  )}
                  {(fundLedger.retention_held || 0) > 0 && isAdmin && !fundLedger.retention_releasable && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <Lock size={10} />
                      Unlocks after 95% collection
                    </p>
                  )}
                </div>
                {fundLedger.next_installment_stage && (
                  <div className="text-sm">
                    <p className="text-stone-500">Next Payment Due</p>
                    <p className="font-semibold capitalize">{fundLedger.next_installment_stage.replace('_', ' ').replace('remaining advance', 'Remaining Advance')}</p>
                    <p className="text-xs text-amber-600">{formatCurrency(fundLedger.next_installment_amount)}</p>
                  </div>
                )}
                <div className="text-sm">
                  <p className="text-stone-500">Payment Structure</p>
                  <p className="text-xs text-stone-600">
                    Token {fundLedger.token_percentage || 5}% + Adv {(fundLedger.payment_structure.advance || 30) - (fundLedger.token_percentage || 5)}% + S1 {fundLedger.payment_structure.stage_1}% + S2 {fundLedger.payment_structure.stage_2}% + S3 {fundLedger.payment_structure.stage_3}% + Ret {fundLedger.payment_structure.retention}%
                  </p>
                  <p className="text-xs text-stone-400 mt-0.5">Token is part of advance (not additional)</p>
                </div>
              </div>

              {/* Red Alert */}
              {fundLedger.red_alert && (
                <div className="p-3 bg-red-100 rounded-lg border border-red-300 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="text-red-600" size={20} />
                    <p className="text-sm font-medium text-red-800">{fundLedger.alert_message}</p>
                  </div>
                  <Button size="sm" variant="destructive" onClick={() => { setEmailTemplateType('payment_request'); setEmailComposeOpen(true); }} data-testid="project-generate-payment-request" className="gap-1.5">
                    <Mail size={14} /> Generate Payment Request
                  </Button>
                </div>
              )}

              {/* Scheduled Payment Reminders */}
              {isAdmin && (
                <div className="border-t border-stone-200 pt-4 mt-2">
                  <PaymentReminders projectId={id} clientEmail={fundLedger?.client_name || ''} />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* iter-338i+: Profitability Pulse — Estimated vs Actual margin across all BOQs */}
        {isAdmin && <ProfitabilityTrendCard projectId={id} />}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="project-dashboard-tabs">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            {isAdmin && <TabsTrigger value="costs">Cost Analysis</TabsTrigger>}
            <TabsTrigger value="approvals">Approvals</TabsTrigger>
            <TabsTrigger value="bills">Bills</TabsTrigger>
            {isAdmin && <TabsTrigger value="vendors">Vendor Activity</TabsTrigger>}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* iter-323/324: Execution Heatmap + progress-driven payment demand */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                <ProgressHeatmap items={heatmapItems} boqId={heatmapBoqId} title="Execution heatmap (active BOQ)" />
                {(() => {
                  const realItems = (heatmapItems || []).filter(i => !i?.is_header && (i?.item_type || 'item') !== 'header');
                  if (!realItems.length) return null;
                  const totalProgress = realItems.reduce((a, i) => a + (i.task_progress || 0), 0);
                  const overallPct = Math.round(totalProgress / realItems.length);
                  // Stage thresholds (advance / S1 / S2 / S3) — tied to payment_structure if available
                  const ps = fundLedger?.payment_structure || {};
                  const milestones = [
                    { pct: 25, label: 'Advance (25%)', stage_pct: ps.advance, hint: 'Trigger Stage Advance payment demand' },
                    { pct: 50, label: 'Stage 1 (50%)', stage_pct: ps.stage_1, hint: 'Trigger Stage 1 payment demand' },
                    { pct: 75, label: 'Stage 2 (75%)', stage_pct: ps.stage_2, hint: 'Trigger Stage 2 payment demand' },
                    { pct: 100, label: 'Final + Retention', stage_pct: ps.stage_3, hint: 'Trigger Final payment + Retention demand' },
                  ];
                  const dueMilestones = milestones.filter(m => overallPct >= m.pct);
                  return (
                    <div className="border-t border-stone-100 pt-3 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-xs text-stone-500">
                          Overall progress: <span className="font-semibold text-stone-800">{overallPct}%</span>
                          {dueMilestones.length > 0 && (
                            <span className="ml-2"> · Milestones reached: <span className="font-medium text-emerald-700">{dueMilestones.map(m => m.label).join(', ')}</span></span>
                          )}
                        </p>
                        {isAdmin && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => { setEmailTemplateType('payment_request'); setEmailComposeOpen(true); }}
                              data-testid="heatmap-demand-payment"
                              title="Compose payment-demand email to client"
                            >
                              <Mail size={14} className="mr-1.5" />
                              Demand Payment
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={() => { setEmailTemplateType('payment_reminder'); setEmailComposeOpen(true); }}
                              data-testid="heatmap-schedule-reminder"
                              title="Schedule a payment reminder email"
                            >
                              <CalendarClock size={14} className="mr-1.5" />
                              Schedule Reminder
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Task Progress */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckSquare size={18} />
                    Task Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">
                        {dashboard.task_summary?.completed || 0} / {dashboard.task_summary?.total || 0}
                      </span>
                      <Badge className="bg-blue-100 text-blue-700">
                        {dashboard.task_summary?.completion_percentage || 0}% Complete
                      </Badge>
                    </div>
                    <Progress 
                      value={dashboard.task_summary?.completion_percentage || 0} 
                      className="h-3"
                    />
                    <div className="grid grid-cols-4 gap-2 text-center text-sm">
                      <div className="p-2 bg-gray-50 rounded">
                        <p className="font-bold text-gray-600">{dashboard.task_summary?.not_started || 0}</p>
                        <p className="text-xs text-gray-500">Not Started</p>
                      </div>
                      <div className="p-2 bg-blue-50 rounded">
                        <p className="font-bold text-blue-600">{dashboard.task_summary?.in_progress || 0}</p>
                        <p className="text-xs text-blue-500">In Progress</p>
                      </div>
                      <div className="p-2 bg-green-50 rounded">
                        <p className="font-bold text-green-600">{dashboard.task_summary?.completed || 0}</p>
                        <p className="text-xs text-green-500">Completed</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded">
                        <p className="font-bold text-red-600">{dashboard.task_summary?.delayed || 0}</p>
                        <p className="text-xs text-red-500">Delayed</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Task Status Pie Chart */}
              {taskStatusData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart size={18} />
                      Task Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <RechartsPie>
                        <Pie
                          data={taskStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {taskStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value, 'Tasks']} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Pending Approvals Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock size={18} />
                    Pending Approvals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {isAdmin && (
                      <>
                        <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="text-amber-600" size={20} />
                            <span>Quotations</span>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700">
                            {dashboard.pending_counts?.quotations || 0}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <IndianRupee className="text-blue-600" size={20} />
                            <span>Vendor Rates</span>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">
                            {dashboard.pending_counts?.rates || 0}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CheckSquare className="text-purple-600" size={20} />
                            <span>Task Approvals</span>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">
                            {dashboard.pending_counts?.tasks || 0}
                          </Badge>
                        </div>
                      </>
                    )}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Receipt className="text-green-600" size={20} />
                        <span>Bills</span>
                      </div>
                      <Badge className="bg-green-100 text-green-700">
                        {dashboard.pending_counts?.bills || 0}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bill Status Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt size={18} />
                    Bill Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-stone-600">Total Bills</span>
                      <span className="font-bold">{dashboard.bill_status?.total_bills || 0}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{dashboard.bill_status?.paid || 0}</p>
                        <p className="text-xs text-green-600">Paid</p>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{dashboard.bill_status?.pending || 0}</p>
                        <p className="text-xs text-yellow-600">Pending</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{dashboard.bill_status?.overdue || 0}</p>
                        <p className="text-xs text-red-600">Overdue</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Project Documents</h3>
                <p className="text-sm text-stone-500">Upload drawings, images, and link cloud documents</p>
              </div>
              <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus size={16} />
                    Add Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add Project Document</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddDocument} className="space-y-4">
                    {/* Upload Type Selection */}
                    <div className="grid grid-cols-4 gap-2">
                      <Button
                        type="button"
                        variant={uploadType === 'link' ? 'default' : 'outline'}
                        onClick={() => setUploadType('link')}
                        className="flex flex-col items-center p-3 h-auto"
                      >
                        <Link size={20} />
                        <span className="text-xs mt-1">URL Link</span>
                      </Button>
                      <Button
                        type="button"
                        variant={uploadType === 'gdrive' ? 'default' : 'outline'}
                        onClick={() => setUploadType('gdrive')}
                        className="flex flex-col items-center p-3 h-auto"
                      >
                        <FolderOpen size={20} />
                        <span className="text-xs mt-1">G-Drive</span>
                      </Button>
                      <Button
                        type="button"
                        variant={uploadType === 'onedrive' ? 'default' : 'outline'}
                        onClick={() => setUploadType('onedrive')}
                        className="flex flex-col items-center p-3 h-auto"
                      >
                        <FolderOpen size={20} />
                        <span className="text-xs mt-1">OneDrive</span>
                      </Button>
                      <Button
                        type="button"
                        variant={uploadType === 'upload' ? 'default' : 'outline'}
                        onClick={() => setUploadType('upload')}
                        className="flex flex-col items-center p-3 h-auto"
                        disabled
                      >
                        <Upload size={20} />
                        <span className="text-xs mt-1">Upload</span>
                      </Button>
                    </div>
                    
                    {uploadType === 'gdrive' && (
                      <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                        <p className="font-medium mb-1">How to get Google Drive link:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li>Open your file in Google Drive</li>
                          <li>Click "Share" → "Anyone with the link"</li>
                          <li>Copy the link and paste below</li>
                        </ol>
                      </div>
                    )}
                    
                    {uploadType === 'onedrive' && (
                      <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                        <p className="font-medium mb-1">How to get OneDrive link:</p>
                        <ol className="list-decimal list-inside space-y-1 text-xs">
                          <li>Open your file in OneDrive</li>
                          <li>Click "Share" → "Copy link"</li>
                          <li>Paste the link below</li>
                        </ol>
                      </div>
                    )}

                    <div>
                      <Label>Document Name *</Label>
                      <Input
                        value={documentForm.name}
                        onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                        placeholder="e.g., Floor Plan - Ground Floor"
                      />
                    </div>
                    
                    <div>
                      <Label>{uploadType === 'gdrive' ? 'Google Drive Link *' : uploadType === 'onedrive' ? 'OneDrive Link *' : 'Document URL *'}</Label>
                      <Input
                        value={documentForm.url}
                        onChange={(e) => setDocumentForm({ ...documentForm, url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                    
                    <div>
                      <Label>Document Type</Label>
                      <Select
                        value={documentForm.doc_type}
                        onValueChange={(value) => setDocumentForm({ ...documentForm, doc_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="drawing">Drawing / Plan</SelectItem>
                          <SelectItem value="image">Site Photo</SelectItem>
                          <SelectItem value="3d_render">3D Render</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="invoice">Invoice</SelectItem>
                          <SelectItem value="specification">Specification</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-3 justify-end">
                      <Button type="button" variant="outline" onClick={() => setDocumentDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" disabled={uploading}>
                        {uploading ? 'Adding...' : 'Add Document'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Documents Grid */}
            {documents.length === 0 ? (
              <Card className="p-8 text-center">
                <FolderOpen size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500 mb-2">No documents uploaded yet</p>
                <p className="text-sm text-stone-400">Add drawings, site photos, and other project documents</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <Card key={doc.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {doc.doc_type === 'image' || doc.doc_type === '3d_render' ? (
                            <Image size={20} className="text-purple-600" />
                          ) : doc.doc_type === 'drawing' ? (
                            <FileSpreadsheet size={20} className="text-blue-600" />
                          ) : (
                            <FileText size={20} className="text-stone-600" />
                          )}
                          <Badge variant="outline" className="text-xs">
                            {doc.source === 'gdrive' ? 'Google Drive' : doc.source === 'onedrive' ? 'OneDrive' : 'Link'}
                          </Badge>
                        </div>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1 text-red-400 hover:text-red-600 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <h4 className="font-medium text-stone-900 mb-1 line-clamp-1">{doc.name}</h4>
                      <p className="text-xs text-stone-500 mb-3 capitalize">{doc.doc_type?.replace('_', ' ')}</p>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink size={14} />
                        Open Document
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Cost Analysis Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="costs" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cost Comparison Chart */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 size={18} />
                      Cost Comparison: Budget vs Planned vs Committed vs Actual
                    </CardTitle>
                    <CardDescription>
                      Track how costs flow from budget to final payments
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={costComparisonData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => `₹${(v/100000).toFixed(1)}L`} />
                        <YAxis type="category" dataKey="name" width={100} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {costComparisonData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Cost Summary Cards */}
                <Card>
                  <CardHeader>
                    <CardTitle>Cost Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <span className="text-stone-600">Project Budget</span>
                      <span className="font-bold text-amber-700">{formatCurrency(dashboard.cost_summary?.budget)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-stone-600">BOQ Planned</span>
                      <span className="font-bold text-blue-700">{formatCurrency(dashboard.cost_summary?.boq_planned)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-stone-600">Quotations Committed</span>
                      <span className="font-bold text-green-700">{formatCurrency(dashboard.cost_summary?.quotations_committed)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-stone-600">Total Invoiced</span>
                      <span className="font-bold text-purple-700">{formatCurrency(dashboard.cost_summary?.invoiced_total)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <span className="text-stone-600">Payments Completed</span>
                      <span className="font-bold text-emerald-700">{formatCurrency(dashboard.cost_summary?.payments_completed)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Category Breakdown */}
                {categoryData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Category-wise BOQ Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <RechartsPie>
                          <Pie
                            data={categoryData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {categoryData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Forward Markup Margins Dashboard */}
                {dashboard.markup_margins && (
                  <Card className="col-span-1 lg:col-span-2" data-testid="markup-margins-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        Markup Margins (Vendor → Admin → Client)
                      </CardTitle>
                      <p className="text-xs text-stone-400">
                        Aggregate profit margins across all BOQs | Default: Admin +{dashboard.markup_margins.admin_markup_default}% | Client +{dashboard.markup_margins.client_markup_default}%
                      </p>
                    </CardHeader>
                    <CardContent>
                      {/* Rate Flow Visual */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                          <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Vendor Total</p>
                          <p className="text-2xl font-bold text-purple-700 mt-1" data-testid="vendor-total">
                            {formatCurrency(dashboard.markup_margins.vendor_total)}
                          </p>
                          <p className="text-xs text-purple-400 mt-1">Base cost</p>
                        </div>
                        <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-center relative">
                          <div className="absolute -left-3 top-1/2 -translate-y-1/2 bg-white border border-stone-200 rounded-full w-5 h-5 flex items-center justify-center text-xs text-stone-400">→</div>
                          <p className="text-xs text-stone-600 font-medium uppercase tracking-wide">Admin Total</p>
                          <p className="text-2xl font-bold text-stone-800 mt-1" data-testid="admin-total">
                            {formatCurrency(dashboard.markup_margins.admin_total)}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            +{formatCurrency(dashboard.markup_margins.admin_margin)} ({dashboard.markup_margins.admin_margin_pct}%)
                          </p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center relative">
                          <div className="absolute -left-3 top-1/2 -translate-y-1/2 bg-white border border-green-200 rounded-full w-5 h-5 flex items-center justify-center text-xs text-green-400">→</div>
                          <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Client Total</p>
                          <p className="text-2xl font-bold text-green-700 mt-1" data-testid="client-total">
                            {formatCurrency(dashboard.markup_margins.client_total)}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            +{formatCurrency(dashboard.markup_margins.client_margin)} ({dashboard.markup_margins.client_margin_pct}%)
                          </p>
                        </div>
                      </div>
                      {/* Total Margin Summary */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 via-stone-50 to-green-50 rounded-lg p-3 border">
                        <span className="text-sm text-stone-600">Total Margin (Vendor → Client)</span>
                        <div className="text-right">
                          <span className="text-xl font-bold text-green-700" data-testid="total-margin">
                            {formatCurrency(dashboard.markup_margins.total_margin)}
                          </span>
                          <span className="text-sm text-stone-500 ml-2">({dashboard.markup_margins.total_margin_pct}%)</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Quotations */}
              {isAdmin && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText size={18} />
                      Pending Quotations ({dashboard.pending_approvals?.quotations?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.pending_approvals?.quotations?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboard.pending_approvals.quotations.map((quot) => (
                          <div key={quot.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                            <div>
                              <p className="font-medium">{quot.quotation_number}</p>
                              <p className="text-sm text-stone-500">
                                {formatDate(new Date(quot.created_at))}
                              </p>
                            </div>
                            <span className="font-bold text-amber-600">{formatCurrency(quot.total)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-stone-500 text-center py-4">No pending quotations</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending Bills */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt size={18} />
                    Pending Bills ({dashboard.pending_approvals?.bills?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard.pending_approvals?.bills?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.pending_approvals.bills.map((bill) => (
                        <div key={bill.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                          <div>
                            <p className="font-medium">{bill.invoice_number}</p>
                            <p className="text-sm text-stone-500">
                              {formatDate(new Date(bill.created_at))}
                            </p>
                          </div>
                          <span className="font-bold text-green-600">{formatCurrency(bill.total)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-4">No pending bills</p>
                  )}
                </CardContent>
              </Card>

              {/* Pending Task Approvals */}
              {isAdmin && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckSquare size={18} />
                      Tasks Pending Approval ({dashboard.pending_approvals?.tasks?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.pending_approvals?.tasks?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboard.pending_approvals.tasks.map((task) => (
                          <div key={task.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                            <div>
                              <p className="font-medium">{task.title}</p>
                              <p className="text-sm text-stone-500">
                                Completed: {task.completed_at ? formatDate(new Date(task.completed_at)) : 'N/A'}
                              </p>
                            </div>
                            <Badge className="bg-yellow-100 text-yellow-700">Awaiting Approval</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-stone-500 text-center py-4">No tasks pending approval</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending Vendor Rates */}
              {isAdmin && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <IndianRupee size={18} />
                      Pending Vendor Rates ({dashboard.pending_approvals?.rates?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.pending_approvals?.rates?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboard.pending_approvals.rates.map((rate) => (
                          <div key={rate.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                            <div>
                              <p className="font-medium">{rate.item_name}</p>
                              <p className="text-sm text-stone-500">{rate.category_name}</p>
                            </div>
                            <span className="font-bold text-blue-600">{formatCurrency(rate.rate)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-stone-500 text-center py-4">No pending rates</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Bills Tab */}
          <TabsContent value="bills" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt size={18} />
                  Recent Bills
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard.bill_status?.recent_bills?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboard.bill_status.recent_bills.map((bill) => (
                      <div key={bill.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <Receipt className="text-stone-400" size={24} />
                          <div>
                            <p className="font-medium">{bill.invoice_number}</p>
                            <p className="text-sm text-stone-500">
                              {formatDate(new Date(bill.created_at))}
                              {bill.due_date && ` • Due: ${formatDate(new Date(bill.due_date))}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-bold">{formatCurrency(bill.total)}</span>
                          <Badge className={getStatusColor(bill.status)}>
                            {bill.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-500 text-center py-8">No bills found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendor Activity Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="vendors" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{dashboard.vendor_activity?.total_vendors || 0}</p>
                    <p className="text-sm text-stone-500">Total Vendors</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <UserCheck className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{dashboard.vendor_activity?.active_vendors || 0}</p>
                    <p className="text-sm text-stone-500">Active (Last 7 days)</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Activity className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">
                      {dashboard.vendor_activity?.vendors?.reduce((sum, v) => sum + v.rates_submitted, 0) || 0}
                    </p>
                    <p className="text-sm text-stone-500">Total Rates Submitted</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Vendor Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard.vendor_activity?.vendors?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.vendor_activity.vendors.map((vendor) => (
                        <div key={vendor.vendor_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <Users className="text-purple-600" size={20} />
                            </div>
                            <div>
                              <p className="font-medium">{vendor.vendor_name}</p>
                              <p className="text-sm text-stone-500">{vendor.vendor_email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <p className="font-bold text-blue-600">{vendor.rates_submitted}</p>
                              <p className="text-xs text-stone-500">Rates</p>
                            </div>
                            <div className="text-center">
                              <p className="font-bold text-green-600">{vendor.tasks_completed}/{vendor.tasks_assigned}</p>
                              <p className="text-xs text-stone-500">Tasks</p>
                            </div>
                            <div className="text-center">
                              <p className="font-bold">{vendor.completion_rate}%</p>
                              <p className="text-xs text-stone-500">Completion</p>
                            </div>
                            <Badge className={getStatusColor(vendor.status)}>
                              {vendor.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-center py-8">No vendors assigned to this project</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Vendor's Own Activity (Vendor View) */}
          {isVendor && dashboard.vendor_activity && (
            <TabsContent value="overview">
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity size={18} />
                    My Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{dashboard.vendor_activity.my_rates_submitted}</p>
                      <p className="text-sm text-blue-600">Rates Submitted</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">{dashboard.vendor_activity.my_tasks_assigned}</p>
                      <p className="text-sm text-purple-600">Tasks Assigned</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{dashboard.vendor_activity.my_tasks_completed}</p>
                      <p className="text-sm text-green-600">Tasks Completed</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-600">{dashboard.vendor_activity.my_tasks_pending}</p>
                      <p className="text-sm text-yellow-600">Tasks Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
      
      {/* Revert Token Dialog */}
      <Dialog open={revertTokenDialogOpen} onOpenChange={setRevertTokenDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Lock size={20} />
              Revert Token Payment Status
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will mark the token payment as NOT received and LOCK execution. 
                Use this only if the token was marked by mistake or recorded incorrectly.
              </p>
            </div>
            <div>
              <Label>Reason for reverting *</Label>
              <Input
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                placeholder="Enter reason (minimum 10 characters)..."
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setRevertTokenDialogOpen(false); setRevertReason(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRevertTokenReceived}
              disabled={reverting || revertReason.trim().length < 10}
              className="bg-red-600 hover:bg-red-700"
            >
              {reverting ? 'Reverting...' : 'Revert & Lock Execution'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Record Client Payment Dialog */}
      <Dialog open={paymentReceivedDialogOpen} onOpenChange={setPaymentReceivedDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Plus size={20} />
              Record Client Payment Received
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                Record payment received from client. Select a payment stage or enter a custom amount.
              </p>
            </div>
            
            {/* Payment Stages Selection */}
            {fundLedger?.payment_stages && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Select Payment Stage</Label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-lg p-2 bg-stone-50">
                  {fundLedger.payment_stages.map((stage) => {
                    const isPaid = stage.paid === true || stage.status === 'paid';
                    const isLocked = stage.status === 'locked';
                    const isDisabled = isPaid || isLocked;
                    
                    return (
                      <div 
                        key={stage.name}
                        onClick={() => {
                          if (!isDisabled) {
                            setSelectedPaymentStage(stage);
                            setPaymentAmount(stage.amount.toString());
                            setPaymentNotes(`${stage.label} - ${stage.description}`);
                          }
                        }}
                        className={`p-3 rounded-lg border transition-all ${
                          isDisabled 
                            ? 'border-stone-200 bg-stone-100 cursor-not-allowed opacity-60' 
                            : selectedPaymentStage?.name === stage.name 
                              ? 'border-green-500 bg-green-50 ring-2 ring-green-200 cursor-pointer' 
                              : 'border-stone-200 bg-white hover:border-green-300 hover:bg-green-50/50 cursor-pointer'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className={`font-medium ${isDisabled ? 'text-stone-400' : 'text-stone-800'}`}>
                              {stage.label}
                              {isPaid && <span className="ml-2 text-xs text-green-600 font-normal">✓ Paid</span>}
                              {isLocked && <span className="ml-2 text-xs text-amber-600 font-normal">🔒 Locked</span>}
                            </p>
                            <p className="text-xs text-stone-500">{stage.description}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${isDisabled ? 'text-stone-400' : 'text-green-700'}`}>
                              ₹{stage.amount?.toLocaleString('en-IN')}
                            </p>
                            <p className="text-xs text-stone-400">{stage.percentage}%</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Custom Amount Option */}
                  <div 
                    onClick={() => {
                      setSelectedPaymentStage({ name: 'custom', label: 'Custom Amount' });
                      setPaymentAmount('');
                      setPaymentNotes('');
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedPaymentStage?.name === 'custom' 
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                        : 'border-dashed border-stone-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-stone-800">Custom Amount</p>
                        <p className="text-xs text-stone-500">Enter a different amount</p>
                      </div>
                      <Edit size={16} className="text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="100"
                value={paymentAmount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  setPaymentAmount(e.target.value);
                  if (selectedPaymentStage?.name !== 'custom') {
                    setSelectedPaymentStage({ name: 'custom', label: 'Custom Amount' });
                  }
                }}
                placeholder="Enter amount received"
                className="mt-1"
              />
            </div>
            
            {/* Payment Mode Selection - NEW */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Payment Mode *</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMode('online')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    paymentMode === 'online'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <CreditCard size={18} />
                    <span className="font-medium">Online</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">Bank Transfer / UPI / Card</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMode('cash')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    paymentMode === 'cash'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Banknote size={18} />
                    <span className="font-medium">Cash</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">Physical Cash Payment</p>
                </button>
              </div>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g., Invoice #123, Stage 2 payment"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
                data-testid="client-payment-date"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setPaymentReceivedDialogOpen(false); setPaymentAmount(''); setPaymentNotes(''); setSelectedPaymentStage(null); setPaymentDate(new Date().toISOString().split('T')[0]); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordPaymentReceived}
              disabled={paymentProcessing || !paymentAmount || parseFloat(paymentAmount) <= 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {paymentProcessing ? 'Recording...' : 'Record Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Record Vendor Payment Dialog */}
      <Dialog open={vendorPaymentDialogOpen} onOpenChange={setVendorPaymentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Plus size={20} />
              Record Vendor Payment Made
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Record payment made to vendor/contractor. Select a preset amount or enter a custom value.
              </p>
            </div>
            
            {/* Vendor Name Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Vendor / Contractor Name *</Label>
              {showCreateVendorForm ? (
                /* Inline Create New Vendor Form */
                <div className="border border-stone-200 rounded-lg p-3 space-y-3 bg-stone-50">
                  <p className="text-xs font-semibold text-stone-700 flex items-center gap-1"><Plus size={12} /> Create New Vendor</p>
                  <Input
                    value={newVendor.name}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Vendor Name *"
                    autoFocus
                    data-testid="new-vendor-name"
                  />
                  <Input
                    value={newVendor.email}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email (optional)"
                    type="email"
                    data-testid="new-vendor-email"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={newVendor.phone}
                      onChange={(e) => setNewVendor(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Phone"
                      data-testid="new-vendor-phone"
                    />
                    <Input
                      value={newVendor.company_name}
                      onChange={(e) => setNewVendor(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Company Name"
                      data-testid="new-vendor-company"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateVendorInline} disabled={creatingVendor || !newVendor.name?.trim()} className="bg-green-600 hover:bg-green-700" data-testid="confirm-create-vendor">
                      {creatingVendor ? 'Creating...' : 'Create & Select'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowCreateVendorForm(false); setNewVendor({ name: '', email: '', phone: '', company_name: '' }); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : !showCustomVendorInput ? (
                <select
                  value={vendorPaymentId}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setVendorPaymentName('');
                      setVendorPaymentId('');
                      setShowCustomVendorInput(true);
                    } else if (e.target.value === '__create__') {
                      setShowCreateVendorForm(true);
                    } else {
                      const v = allVendors.find(v => v.id === e.target.value) || 
                                dashboard?.vendor_activity?.vendors?.find(v => v.vendor_id === e.target.value);
                      setVendorPaymentId(e.target.value);
                      setVendorPaymentName(v?.name || v?.vendor_name || e.target.value);
                    }
                  }}
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="vendor-payment-name-select"
                >
                  <option value="">Select vendor...</option>
                  {allVendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}{v.company_name ? ` (${v.company_name})` : ''}</option>
                  ))}
                  {/* Also show project-specific vendors not in directory */}
                  {dashboard?.vendor_activity?.vendors?.filter(pv => 
                    !allVendors.find(av => av.id === pv.vendor_id)
                  ).map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>
                  ))}
                  <option value="__create__" style={{ fontWeight: 600, color: '#16a34a' }}>+ Create New Vendor</option>
                  <option value="__custom__">Other (type manually)</option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={vendorPaymentName}
                    onChange={(e) => setVendorPaymentName(e.target.value)}
                    placeholder="Enter vendor/contractor name"
                    data-testid="vendor-payment-name-input"
                    autoFocus={showCustomVendorInput}
                  />
                  {showCustomVendorInput && (
                    <Button variant="outline" size="sm" onClick={() => { setShowCustomVendorInput(false); setVendorPaymentName(''); setVendorPaymentId(''); }}>
                      Back
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {/* Quick Amount Selection for Vendors - Grey out stages that have been paid */}
            {fundLedger?.payment_stages && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Quick Amount Selection (based on contract stages)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {fundLedger.payment_stages.filter(s => s.name !== 'retention').slice(0, 4).map((stage) => {
                    // Check if this stage amount has been paid to vendors
                    const vendorPayments = paymentLedger?.vendor_payments || [];
                    const totalVendorPaid = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
                    
                    // Calculate cumulative stage totals to determine if stage is "covered"
                    const stageIndex = fundLedger.payment_stages.findIndex(s => s.name === stage.name);
                    let cumulativeAmount = 0;
                    for (let i = 0; i <= stageIndex; i++) {
                      if (fundLedger.payment_stages[i].name !== 'retention') {
                        cumulativeAmount += fundLedger.payment_stages[i].amount;
                      }
                    }
                    
                    const isPaidForVendor = totalVendorPaid >= cumulativeAmount;
                    const isDisabled = isPaidForVendor;
                    
                    return (
                      <button
                        key={stage.name}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setPaymentAmount(stage.amount.toString());
                            setPaymentNotes(`Vendor payment - ${stage.label}`);
                          }
                        }}
                        className={`p-2 text-left rounded-lg border transition-all ${
                          isDisabled 
                            ? 'border-stone-200 bg-stone-100 cursor-not-allowed opacity-60'
                            : paymentAmount === stage.amount.toString() 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-stone-200 bg-white hover:border-red-300 hover:bg-red-50/50'
                        }`}
                      >
                        <p className={`font-medium text-sm ${isDisabled ? 'text-stone-400' : 'text-stone-700'}`}>
                          ₹{stage.amount?.toLocaleString('en-IN')}
                          {isPaidForVendor && <span className="ml-1 text-green-600 text-xs">✓ Paid</span>}
                        </p>
                        <p className="text-xs text-stone-500">{stage.percentage}% - {stage.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="100"
                value={paymentAmount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount paid"
                className="mt-1"
              />
              <p className="text-xs text-stone-400 mt-1">
                Available Fund: {formatCurrency(fundLedger?.available_fund || 0)}
              </p>
              <CommissionPreviewChip amountRupees={paymentAmount} />
            </div>
            
            {/* Payment Mode Selection - NEW */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Payment Mode *</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMode('online')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    paymentMode === 'online'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-red-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <CreditCard size={18} />
                    <span className="font-medium">Online</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">Bank Transfer / UPI / Card</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMode('cash')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    paymentMode === 'cash'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-red-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Banknote size={18} />
                    <span className="font-medium">Cash</span>
                  </div>
                  <p className="text-xs mt-1 opacity-70">Physical Cash Payment</p>
                </button>
              </div>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="e.g., Vendor name, PO #456, Material purchase"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
                data-testid="vendor-payment-date"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setVendorPaymentDialogOpen(false); setPaymentAmount(''); setPaymentNotes(''); setVendorPaymentName(''); setShowCustomVendorInput(false); setShowCreateVendorForm(false); setNewVendor({ name: '', email: '', phone: '', company_name: '' }); setPaymentDate(new Date().toISOString().split('T')[0]); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordVendorPayment}
              disabled={paymentProcessing || !paymentAmount || parseFloat(paymentAmount) <= 0}
              className="bg-red-600 hover:bg-red-700"
            >
              {paymentProcessing ? 'Recording...' : 'Record Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Ledger Dialog */}
      <Dialog open={paymentLedgerDialogOpen} onOpenChange={setPaymentLedgerDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} />
              Payment Ledger - {dashboard?.project?.name}
            </DialogTitle>
          </DialogHeader>
          
          {paymentLedger && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600">Client Payments Received</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(paymentLedger.summary?.total_client_received)}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">Vendor Payments Made</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(paymentLedger.summary?.total_vendor_paid)}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">Net Balance</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(paymentLedger.summary?.net_balance)}</p>
                </div>
              </div>

              <Tabs defaultValue="client">
                <TabsList>
                  <TabsTrigger value="client">Client Payments ({paymentLedger.client_payments?.filter(p => p.status !== 'cancelled').length || 0})</TabsTrigger>
                  <TabsTrigger value="vendor">Vendor Payments ({paymentLedger.vendor_payments?.filter(p => p.status !== 'cancelled').length || 0})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="client" className="mt-4">
                  {paymentLedger.client_payments?.length > 0 ? (
                    <div className="space-y-3">
                      {paymentLedger.client_payments.filter(p => p.status !== 'cancelled').map(payment => (
                        <div key={payment.id} className="p-4 border rounded-lg bg-white flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-green-700">{formatCurrency(payment.amount)}</span>
                              <Badge variant="outline" className="text-xs">Received from Client</Badge>
                              {payment.payment_mode && (
                                <Badge variant="outline" className="text-xs capitalize">{payment.payment_mode}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-stone-500 mt-1">{payment.notes}</p>
                            <p className="text-xs text-stone-400">
                              {payment.payment_date ? formatDate(new Date(payment.payment_date)) : formatDate(new Date(payment.created_at))} • {new Date(payment.created_at).toLocaleTimeString('en-IN')} • By {payment.recorded_by_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDownloadReceipt(payment.id)}
                              className="text-blue-600"
                            >
                              <Receipt size={14} className="mr-1" />
                              Receipt
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedPayment(payment);
                                setPaymentAmount(payment.amount.toString());
                                setPaymentNotes(payment.notes);
                                setEditPaymentMode(payment.payment_mode || 'online');
                                setEditPaymentDate(payment.payment_date ? payment.payment_date.split('T')[0] : (payment.created_at ? payment.created_at.split('T')[0] : ''));
                                setAmendVendorName(payment.vendor_name || '');
                                setAmendVendorId(payment.vendor_id || '');
                                setAmendReason('');
                                setEditPaymentDialogOpen(true);
                                fetchAllVendors();
                              }}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-stone-500">No client payments recorded yet.</p>
                  )}
                </TabsContent>

                <TabsContent value="vendor" className="mt-4">
                  {paymentLedger.vendor_payments?.length > 0 ? (
                    <div className="space-y-3">
                      {paymentLedger.vendor_payments.filter(p => p.status !== 'cancelled').map(payment => (
                        <div key={payment.id} className="p-4 border rounded-lg bg-white flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-red-700">{formatCurrency(payment.amount)}</span>
                              <Badge variant="outline" className="text-xs">Paid to {payment.vendor_name || 'Vendor'}</Badge>
                              {payment.payment_mode && (
                                <Badge variant="outline" className="text-xs capitalize">{payment.payment_mode}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-stone-500 mt-1">{payment.notes}</p>
                            <p className="text-xs text-stone-400">
                              {payment.payment_date ? formatDate(new Date(payment.payment_date)) : formatDate(new Date(payment.created_at))} • {new Date(payment.created_at).toLocaleTimeString('en-IN')} • By {payment.recorded_by_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDownloadReceipt(payment.id)}
                              className="text-blue-600"
                            >
                              <Receipt size={14} className="mr-1" />
                              Receipt
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedPayment(payment);
                                setPaymentAmount(payment.amount.toString());
                                setPaymentNotes(payment.notes);
                                setEditPaymentMode(payment.payment_mode || 'online');
                                setEditPaymentDate(payment.payment_date ? payment.payment_date.split('T')[0] : (payment.created_at ? payment.created_at.split('T')[0] : ''));
                                setAmendVendorName(payment.vendor_name || '');
                                setAmendVendorId(payment.vendor_id || '');
                                setAmendReason('');
                                setEditPaymentDialogOpen(true);
                                fetchAllVendors();
                              }}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-stone-500">No vendor payments recorded yet.</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialogOpen} onOpenChange={setEditPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Amend Payment Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Vendor Name (editable) */}
            <div>
              <Label>Vendor / Contractor Name</Label>
              <select
                value={amendVendorId}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setAmendVendorId('');
                    setAmendVendorName('');
                  } else {
                    const v = allVendors.find(v => v.id === e.target.value);
                    setAmendVendorId(e.target.value);
                    setAmendVendorName(v?.name || e.target.value);
                  }
                }}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="amend-vendor-select"
              >
                <option value="">Select vendor...</option>
                {allVendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}{v.company_name ? ` (${v.company_name})` : ''}</option>
                ))}
                {/* Show current vendor if not in list */}
                {amendVendorName && !allVendors.find(v => v.id === amendVendorId) && (
                  <option value={amendVendorId || amendVendorName}>{amendVendorName} (current)</option>
                )}
              </select>
            </div>
            <div>
              <Label>Amount ({'\u20B9'})</Label>
              <Input 
                type="number"
                value={paymentAmount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date"
                value={editPaymentDate}
                onChange={(e) => setEditPaymentDate(e.target.value)}
                data-testid="edit-payment-date"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Payment Mode</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditPaymentMode('online')}
                  className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                    editPaymentMode === 'online'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <CreditCard size={16} />
                    <span className="font-medium">Online</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setEditPaymentMode('cash')}
                  className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                    editPaymentMode === 'cash'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-stone-200 bg-white text-stone-600 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Banknote size={16} />
                    <span className="font-medium">Cash</span>
                  </div>
                </button>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input 
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-red-600">Reason for Amendment *</Label>
              <Input 
                value={amendReason}
                onChange={(e) => setAmendReason(e.target.value)}
                placeholder="Enter reason for this change..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditPaymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAmendPayment}
              disabled={paymentProcessing || !amendReason.trim()}
            >
              {paymentProcessing ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Release Retention Dialog */}
      <Dialog open={releaseRetentionDialogOpen} onOpenChange={setReleaseRetentionDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="text-green-600" size={18} />
              Release Retention Amount
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Retention is typically released 30 days after project handover and client sign-off.
              </p>
            </div>
            
            <div>
              <Label htmlFor="retention-amount">Release Amount (₹)</Label>
              <Input
                id="retention-amount"
                type="number"
                value={retentionReleaseAmount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setRetentionReleaseAmount(e.target.value)}
                placeholder="Enter amount to release"
                className="mt-1"
                data-testid="retention-amount-input"
              />
              <p className="text-xs text-stone-500 mt-1">
                Available: {formatCurrency(fundLedger?.retention_held || 0)}
              </p>
            </div>
            
            <div>
              <Label htmlFor="retention-notes">Notes (Optional)</Label>
              <Input
                id="retention-notes"
                value={retentionReleaseNotes}
                onChange={(e) => setRetentionReleaseNotes(e.target.value)}
                placeholder="e.g., Final sign-off received, Defect liability period completed"
                className="mt-1"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReleaseRetentionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReleaseRetention} 
              disabled={releasingRetention}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-release-retention-btn"
            >
              {releasingRetention ? 'Releasing...' : 'Release Retention'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EmailComposeDialog
        open={emailComposeOpen}
        onOpenChange={setEmailComposeOpen}
        projectId={id}
        templateType={emailTemplateType}
      />
    </Shell>
  );
}
